import { DropdownMenu } from '$tablet-app/src/app/components/DropdownMenu/DropdownMenu';
import Row from '$tablet-app/src/app/components/Row';
import Separator from '$tablet-app/src/app/components/Separator';

import { getAppVersion } from '$tablet-app/src/app/utils/version';
import {
  cognitoUserInAnyGroup,
  cognitoUserInGroup,
} from '@akin-home/auth/frontend';
import { useAuthStore } from '@akin-home/auth/react';
import { CognitoUserGroup } from '@akin-home/aws-cognito';
import { useHasLockPIN } from '@akin-home/lock-screen/react';
import { colors } from '@akin-home/theme';
import { showErrorToast, showSuccessToast } from '@akin-home/toast/native';
import { useUpdateUser } from '@akin-home/users/react';
import { isRobotUser } from '@akin-home/utils/native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { Switch } from 'react-native-elements';
import { Sound } from 'react-native-notification-sounds';
import styled from 'styled-components/native';
import SetProfilePinScreen from '../../auth/lock_screen/components/SetProfilePinScreen/SetProfilePinScreen';
import { useChatApplicationID } from '../../chat/stores/useChatApplicationID';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import PressableLabelAction from '../../components/PressableContainer/PressableLabelAction';
import SelectorModal from '../../components/SelectorModal/SelectorModal';
import { Text } from '../../components/Text/Text';
import { AndroidOnly } from '../../platform';
import { useStateStore } from '../../stores/useStateStore';
import { palette } from '../../theme';
import { useCurrentUser } from '../../users';
import { useMicrophonePermission } from '../../utils/permissions/useMicrophonePermission';
import useBool from '../../utils/state/useBool';
import {
  useAlarmSounds,
  useNotificationSounds,
} from '../../utils/systemSounds';
import { BrightnessSlider } from '../brightness/components';
import { ToggleRobotUserDialog } from '../robots/components/ToggleRobotUserDialog';
import { useEditRobotMode } from '../robots/hooks';

interface SettingsScreenState {
  sounds: {
    modalTitle?: string;
    type?: 'alarm' | 'notification';
    options: Sound[];
    showModal: boolean;
  };
  /** ID of the user having their PIN set */
  set_pin_user_id?: string;
}

const application_options = [
  { id: 1, title: 'Pixi Live' },
  { id: 3, title: 'Pixi Dev' },
  { id: 21, title: 'BMC Trials' },
];

const SettingsScreen = () => {
  const auth_user = useAuthStore((state) => state.auth_user);

  const user = useCurrentUser();
  const updateUser = useUpdateUser();

  const voice_enabled = useStateStore((state) => state.tts_enabled);
  const setVoiceEnabled = useStateStore((state) => state.setTTSEnabled);

  const { data: has_pin } = useHasLockPIN({ user_id: user.id });

  const application_id = useChatApplicationID();

  const { granted, request } = useMicrophonePermission();

  const notificationSoundID =
    user.systemPreferences?.androidNotificationSoundID;

  const alarmSoundID = user.systemPreferences?.androidAlarmSoundID;

  const [state, setState] = useState<SettingsScreenState>({
    sounds: {
      options: [],
      showModal: false,
    },
  });

  const alert = useNotificationSounds();
  const alarm = useAlarmSounds();

  const onPressSetPIN = () => setState({ ...state, set_pin_user_id: user.id });

  const onChangePasswordSuccess = () => {
    onCloseResetPasswordModal();
    showSuccessToast({
      text1: 'Success!',
      text2: 'Password changed',
    });
  };

  const [
    showResetPasswordModal,
    onPressResetPassword,
    onCloseResetPasswordModal,
  ] = useBool();

  const onCloseSetPIN = () =>
    setState({ ...state, set_pin_user_id: undefined });

  const onAlarmsClick = () =>
    setState({
      ...state,
      sounds: {
        modalTitle: 'Alarm Sounds',
        type: 'alarm',
        options: alarm.sounds,
        showModal: true,
      },
    });

  const onAlertsClick = () =>
    setState({
      ...state,
      sounds: {
        modalTitle: 'Alert Sounds',
        type: 'notification',
        options: alert.sounds,
        showModal: true,
      },
    });

  const selectSound = async (sound: Sound) => {
    const attrToUpdate =
      state.sounds.type === 'notification'
        ? 'androidNotificationSoundID'
        : 'androidAlarmSoundID';

    const updateInput = {
      id: user.id as string,
      systemPreferences: {
        ...user.systemPreferences,
        [attrToUpdate]: sound.soundID,
      },
    };
    // Remove the typename field that may exist within this update as it causes the graphql query to fail
    delete updateInput?.systemPreferences?.__typename;

    try {
      clearSoundState();
      await updateUser.mutateAsync({ input: updateInput });
    } catch (err) {
      showErrorToast({
        text1: 'Error in Selecting Sound',
        text2: 'Please try again',
      });
    }
  };

  const clearSoundState = () => {
    setState({
      ...state,
      sounds: {
        options: [],
        showModal: false,
        type: undefined,
        modalTitle: undefined,
      },
    });

    stopSound();
  };

  const playSound = (sound: Sound) => alert.play(sound);
  const stopSound = () => alert.stop();

  const version = getAppVersion();

  const updateChatApplicationId = (id: number) => {
    updateUser.mutate({
      input: {
        id: user.id,
        chatApplicationId: String(id),
      },
    });
  };

  const [robotUserDialogVisible, setRobotUserDialogVisible] = useState(false);
  const robotModeMutation = useEditRobotMode({
    onSuccess: () => setRobotUserDialogVisible(false),
  });

  return (
    <Container>
      <HorizontalMargins>
        <AndroidOnly>
          <Text preset="h3">Permissions</Text>

          <PressableLabelAction
            label="Microphone"
            onPress={() => request.mutate()}
          >
            <PressableRow>
              <Text>{granted ? 'Allowed' : 'Not Allowed'}</Text>
              <FontAwesomeIcon icon="microphone" />
            </PressableRow>
          </PressableLabelAction>
        </AndroidOnly>

        <AndroidOnly>
          <Text preset="h3">Sounds</Text>
          {/* Alert Select */}
          <PressableLabelAction label="Alerts" onPress={onAlertsClick}>
            <PressableRow>
              <Text>
                {
                  alert.sounds.find((a) => a.soundID === notificationSoundID)
                    ?.title
                }
              </Text>
              <FontAwesomeIcon icon="chevron-right" />
            </PressableRow>
          </PressableLabelAction>

          {/* Alarm Select */}
          <PressableLabelAction label="Alarms" onPress={onAlarmsClick}>
            <PressableRow>
              <Text>
                {alarm.sounds.find((a) => a.soundID === alarmSoundID)?.title}
              </Text>
              <FontAwesomeIcon icon="chevron-right" />
            </PressableRow>
          </PressableLabelAction>
        </AndroidOnly>

        <AndroidOnly>
          <Text preset="h3">Voice</Text>
          <SwitchRow>
            <Switch
              value={!!voice_enabled}
              onValueChange={setVoiceEnabled}
              color={colors.success[60]}
            />
            <Text>Enable AI voice</Text>
          </SwitchRow>
        </AndroidOnly>

        <Text preset="h3">Security</Text>

        {has_pin ? (
          <PressableLabelAction label="Set PIN" onPress={onPressSetPIN}>
            <PressableRow>
              <Text>{has_pin ? 'PIN is set' : 'No PIN set'}</Text>
              <FontAwesomeIcon icon="lock" />
            </PressableRow>
          </PressableLabelAction>
        ) : null}

        <PressableLabelAction
          label="Change Password"
          onPress={onPressResetPassword}
        >
          <FontAwesomeIcon icon="lock" />
        </PressableLabelAction>

        <AndroidOnly>
          <Text preset="h3">Brightness</Text>
          <BrightnessSlider />
        </AndroidOnly>
      </HorizontalMargins>

      <Separator />

      <HorizontalMargins>
        {cognitoUserInAnyGroup([
          CognitoUserGroup.DEVELOPER,
          CognitoUserGroup.CONTENT_WRITER,
          CognitoUserGroup.ADMIN,
        ])(auth_user) ? (
          <>
            <Text preset="h3">Preferences</Text>
            <Row marginVertical={10}>
              <Text bold>Internal Akin: Select Pixi application</Text>
            </Row>
            <DropdownMenu
              defaultTitle="Select an application to speak to"
              options={application_options}
              onChange={(value) => updateChatApplicationId(value.id as number)}
              value={application_options.find((p) => p.id === application_id)}
            />
          </>
        ) : null}

        {cognitoUserInGroup(CognitoUserGroup.ADMIN)(auth_user) ? (
          <>
            <Text preset="h3">Robots</Text>
            <PressableLabelAction
              label={isRobotUser() ? 'Disable Robot Mode' : 'Enable Robot Mode'}
              onPress={() => setRobotUserDialogVisible(true)}
            />
          </>
        ) : null}

        <Row marginVertical={10}>
          <Text variant="caption">App Version: {version}</Text>
        </Row>
      </HorizontalMargins>

      {/* Modal for selecting sounds */}
      <SelectorModal
        title={state.sounds.modalTitle}
        isVisible={state.sounds.showModal}
        items={state.sounds.options}
        onRequestClose={clearSoundState}
        renderItem={({ item }) => (
          <TouchableOpacity onPress={() => selectSound(item)}>
            <SoundSelectorRow>
              <Text>{item.title}</Text>

              {/* Sample play button */}
              <TouchableOpacity onPress={() => playSound(item)}>
                <Text>
                  <FontAwesomeIcon icon={['fas', 'play-circle']} size={32} />
                </Text>
              </TouchableOpacity>
            </SoundSelectorRow>
          </TouchableOpacity>
        )}
      />

      <SetProfilePinScreen
        user_id={state.set_pin_user_id}
        onClose={onCloseSetPIN}
      />

      {showResetPasswordModal ? (
        <ChangePasswordModal
          onChangeSuccess={onChangePasswordSuccess}
          onClose={onCloseResetPasswordModal}
        />
      ) : null}

      {/* <VoiceSettingsModal /> */}

      <ToggleRobotUserDialog
        visible={robotUserDialogVisible}
        onClose={() => setRobotUserDialogVisible(false)}
        onConfirm={() => robotModeMutation.mutate(user.username)}
        isLoading={robotModeMutation.isLoading}
      />
    </Container>
  );
};

const Container = styled.ScrollView`
  background-color: ${palette.white};
  height: 100%;
`;

const HorizontalMargins = styled.View`
  margin-left: 20px;
  margin-right: 20px;

  gap: 8px;
`;

const PressableRow = styled.View`
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const SoundSelectorRow = styled.View`
  padding: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const SwitchRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export default SettingsScreen;
